import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { SvgIconComponent } from 'angular-svg-icon';
import { NoDataComponent } from '../../../../../pages/content/no-data/no-data.component';
import { CommonModule } from '@angular/common';
import { TreatmentPhasesService } from './treatment-phases.service';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { LoaderService } from '../../../../../core/services/loader.service';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { CommonService } from '../../../../../shared/services/common.service';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzInputModule } from 'ng-zorro-antd/input';
import { Note } from '../insights-data.interface';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ConfirmDialogComponent } from '../../../../../components/confirm-dialog/confirm-dialog.component';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
@Component({
  selector: 'app-treatment-phases',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NzButtonModule,
    NzDatePickerModule,
    NzFormControlComponent,
    NzFormItemComponent,
    SvgIconComponent,
    NzFormLabelComponent,
    NzFormModule,
    NoDataComponent,
    CommonModule,
    NzTableModule,
    NzInputModule,
    NzSpinModule,
    ConfirmDialogComponent,
  ],
  templateUrl: './treatment-phases.component.html',
  styleUrl: './treatment-phases.component.scss',
})
export class TreatmentPhasesComponent implements OnInit {
  private treatmentPhaseService = inject(TreatmentPhasesService);
  treatmentPhaseForm!: FormGroup;
  unitId: number | null = null;
  graphLabels$ = this.treatmentPhaseService.labels$;
  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean> = of(false);
  isEditMode = false;
  errorMessage = '';
  showConfirmDialog = false;
  isDeleting = false;
  selectedId = 0;
  listOfColumn = [
    {
      title: 'Phase Label',
      key: 'labelNote',
    },
    {
      title: 'Phase Date',
      key: 'noteDate',
    },
    { title: '', compare: null },
  ];

  constructor(
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private commonService: CommonService,
    private drawerService: DrawerService,
    private cdr: ChangeDetectorRef
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
  }

  ngOnInit(): void {
    this.treatmentPhaseForm = this.fb.group({
      id: [0],
      labelNote: ['', [Validators.required, Validators.maxLength(20)]],
      noteDate: ['', Validators.required],
    });
    this.loadData();
  }

  setupDrawerSubscription() {
    this.drawerService
      .getParams<number>(DRAWER_IDS.DATA_GRAPH_TREATMENT_PHASE)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.unitId = params;
        }
      });
  }

  onSubmit() {
    if (!this.treatmentPhaseForm.valid) {
      return;
    }
    const payload = {
      id: this.treatmentPhaseForm.value.id,
      unitId: this.unitId,
      labelNote: this.treatmentPhaseForm.value.labelNote,
      noteDate: new Date(this.treatmentPhaseForm.value.noteDate)
        .toISOString()
        .split('T')[0],
    };
    this.loaderService.show();
    const operation$ = this.treatmentPhaseService.createGraphLabel(payload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.isEditMode = false;
          this.cdr.detectChanges();
          this.loadData();
          this.commonService.notify(
            'success',
            `Graph Label ${this.isEditMode ? 'updated' : 'created'} successfully`
          );

          this.treatmentPhaseForm.reset();
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private handleError(error: ErrorResponse): void {
    this.errorMessage = error?.message;
    this.commonService.notify('error', this.errorMessage);
    this.cdr.detectChanges();
  }

  loadData(): void {
    this.loaderService.show();
    if (this.unitId) {
      this.treatmentPhaseService
        .getGraphLabels(this.unitId)
        .pipe(
          take(1), // Take only one response
          finalize(() => this.loaderService.hide()), // End loading after response
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: () => {
            this.cdr.detectChanges();
          },
          error: (error: ErrorResponse) => {
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
          },
        });
    }
  }

  edit(data: Note) {
    this.isEditMode = true;
    this.treatmentPhaseForm.patchValue({
      id: data.id,
      labelNote: data.labelNote,
      noteDate: data.noteDate,
    });
  }
  showDeleteModal(id: number) {
    this.showConfirmDialog = true;
    this.selectedId = id;
  }

  removeRecord() {
    if (this.unitId) {
      this.isDeleting = true;
      const operation$ = this.treatmentPhaseService.deleteGraphLable(
        this.unitId,
        this.selectedId
      );
      operation$
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          finalize(() => {
            this.loaderService.hide();
          })
        )
        .subscribe({
          next: () => {
            this.isDeleting = false;
            this.showConfirmDialog = false;
            this.selectedId = 0;
            this.loadData();
            this.commonService.notify(
              'success',
              'Record deleted successfully.'
            );
          },
          error: (error: ErrorResponse) => {
            this.isDeleting = false;
            this.showConfirmDialog = false;
            this.handleError(error);
          },
        });
    }
  }

  handleCancel() {
    this.showConfirmDialog = false;
    this.isDeleting = false;
  }
}
